import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { compoundManagementSlice, getCompoundManagementModalFilter } from '../../../reducers/compoundManagementSlice';
import { FilterModalButton, FilterModalFooter } from '../../../sharedComponents/filter/FilterModal';
import { LocationFilter } from '../../../sharedComponents/filter/LocationFilter';
import { CompoundManagementView } from '../../compoundManagement.types';
import { LocalCompoundVehicleModalFilter } from './filters/CompoundFilterProps';
import { DispatchTypeFilter } from '../../../sharedComponents/filter/DispatchTypeFilter';
import { FinalDestinationFilter } from './filters/FinalDestinationFilter';
import { LoadIdFilter } from './filters/LoadIdFilter';
import { MeansOfTransportIdFilter } from './filters/MeansOfTransportIdFilter';
import { ArrivalFromFilter } from './filters/ArrivalFromFilter';
import { DepartureToFilter } from './filters/DepartureToFilter';
import { ExcludeCancelledFilter } from './ExcludeCancelledFilter';
import { getValidDate, getValidText } from '../../../sharedComponents/inputValidations';
import { DateRangeFilter } from '../../../sharedComponents/filter/DateRangeFilter';
import { MeansOfTransportTypeFilter } from '../../../sharedComponents/filter/MeansOfTransportTypeFilter';
import { ManufacturerFilter } from '../../../sharedComponents/filter/ManufacturerFilter';
import { validateDateTimeFields } from '../../../sharedComponents/dateHelper';

export const UNSET_COMPOUND_VEHICLE_MODAL_FILTER: LocalCompoundVehicleModalFilter = {
    locationFilter: undefined,
    vehicleTypeFilter: undefined,
    manufacturerFilter: undefined,
    arrivalDateStartAtFilter: undefined,
    arrivalDateEndAtFilter: undefined,
    departureDateStartAtFilter: undefined,
    departureDateEndAtFilter: undefined,
    loadIdFilter: undefined,
    meansOfTransportIdFilter: undefined,
    finalDestinationFilter: undefined,
    arrivalMeansOfTransportTypeFilter: undefined,
    departureMeansOfTransportTypeFilter: undefined,
    arrivalFromFilter: undefined,
    departureToFilter: undefined,
    excludeCanceledFilter: false,
};

export const getIsFilterActive = (
    modalType: CompoundManagementView,
    localFilters: LocalCompoundVehicleModalFilter,
): boolean => {
    const commonFiltersActive =
        localFilters.locationFilter !== undefined ||
        localFilters.vehicleTypeFilter !== undefined ||
        localFilters.manufacturerFilter !== undefined ||
        localFilters.finalDestinationFilter !== undefined ||
        localFilters.excludeCanceledFilter;

    const transportFiltersActive =
        localFilters.loadIdFilter !== undefined || localFilters.meansOfTransportIdFilter !== undefined;

    const arrivalFiltersActive =
        localFilters.arrivalDateStartAtFilter !== undefined ||
        localFilters.arrivalDateEndAtFilter !== undefined ||
        localFilters.arrivalMeansOfTransportTypeFilter !== undefined ||
        localFilters.arrivalFromFilter !== undefined;

    const departureFiltersActive =
        localFilters.departureDateStartAtFilter !== undefined ||
        localFilters.departureDateEndAtFilter !== undefined ||
        localFilters.departureMeansOfTransportTypeFilter !== undefined ||
        localFilters.departureToFilter !== undefined;

    switch (modalType) {
        case CompoundManagementView.INCOMING_VEHICLES:
            return commonFiltersActive || transportFiltersActive || arrivalFiltersActive;
        case CompoundManagementView.INVENTORY:
            return commonFiltersActive || arrivalFiltersActive;
        case CompoundManagementView.OUTGOING_VEHICLES:
            return commonFiltersActive || transportFiltersActive || departureFiltersActive;
        case CompoundManagementView.DEPARTED:
            return commonFiltersActive || arrivalFiltersActive || departureFiltersActive;
    }
};

interface CompoundManagementFilterModalContentProps {
    localFilters: LocalCompoundVehicleModalFilter;
    setLocalFilters: Dispatch<SetStateAction<LocalCompoundVehicleModalFilter>>;
    modalType: CompoundManagementView;
}

// eslint-disable-next-line max-lines-per-function
const CompoundManagementFilterModalContent = ({
    localFilters,
    setLocalFilters,
    modalType,
}: CompoundManagementFilterModalContentProps) => {
    const showTransportFilters =
        modalType === CompoundManagementView.INCOMING_VEHICLES ||
        modalType === CompoundManagementView.OUTGOING_VEHICLES;

    const showArrivalFilters = modalType !== CompoundManagementView.OUTGOING_VEHICLES;
    const showDepartureFilters =
        modalType === CompoundManagementView.DEPARTED || modalType === CompoundManagementView.OUTGOING_VEHICLES;

    return (
        <>
            <div className="margin-top-10">
                <ExcludeCancelledFilter
                    checked={localFilters.excludeCanceledFilter}
                    onChange={(excludeCanceledFilter) => {
                        setLocalFilters((prevState) => ({ ...prevState, excludeCanceledFilter }));
                    }}
                />
            </div>
            {showArrivalFilters && (
                <div className="margin-top-20">
                    <ArrivalFromFilter
                        expected={showTransportFilters}
                        localFilters={localFilters}
                        setLocalFilters={setLocalFilters}
                    />
                </div>
            )}
            <div>
                <LocationFilter
                    value={localFilters.locationFilter}
                    onLocationChange={(locationFilter) => {
                        setLocalFilters((prevState) => ({ ...prevState, locationFilter }));
                    }}
                />
            </div>
            {showDepartureFilters && (
                <div className="margin-top-20">
                    <DepartureToFilter
                        expected={showTransportFilters}
                        localFilters={localFilters}
                        setLocalFilters={setLocalFilters}
                    />
                </div>
            )}

            {showArrivalFilters && (
                <div className="margin-top-20">
                    <MeansOfTransportTypeFilter
                        label="outboundOrderBook.compoundManagement.filter.arrivalMeansOfTransportType"
                        filterName="arrivalMeansOfTransportTypeFilter"
                        localFilters={localFilters}
                        setLocalFilters={setLocalFilters}
                    />
                </div>
            )}
            {showDepartureFilters && (
                <div className="margin-top-20">
                    <MeansOfTransportTypeFilter
                        label="outboundOrderBook.compoundManagement.filter.departureMeansOfTransportType"
                        filterName="departureMeansOfTransportTypeFilter"
                        localFilters={localFilters}
                        setLocalFilters={setLocalFilters}
                    />
                </div>
            )}

            {showTransportFilters && (
                <>
                    <div className="margin-top-20">
                        <MeansOfTransportIdFilter localFilters={localFilters} setLocalFilters={setLocalFilters} />
                    </div>
                    <div className="margin-top-20">
                        <LoadIdFilter localFilters={localFilters} setLocalFilters={setLocalFilters} />
                    </div>

                    <hr />
                </>
            )}

            <div className="margin-top-20">
                <DispatchTypeFilter localFilters={localFilters} setLocalFilters={setLocalFilters} />
            </div>

            <div className="margin-top-20">
                <FinalDestinationFilter localFilters={localFilters} setLocalFilters={setLocalFilters} />
            </div>

            <div className="margin-top-20">
                <ManufacturerFilter localFilters={localFilters} setLocalFilters={setLocalFilters} />
            </div>

            <hr />
            {showArrivalFilters && (
                <div className="margin-top-20">
                    <DateRangeFilter
                        timeframeFilterTitle="outboundOrderBook.common.filter.arrivalTimeframe"
                        startDateFilterName="arrivalDateStartAtFilter"
                        endDateFilterName="arrivalDateEndAtFilter"
                        startDate={localFilters.arrivalDateStartAtFilter}
                        endDate={localFilters.arrivalDateEndAtFilter}
                        setLocalFilters={setLocalFilters}
                    />
                </div>
            )}
            {showDepartureFilters && (
                <div className="margin-top-20">
                    <DateRangeFilter
                        timeframeFilterTitle="outboundOrderBook.common.filter.departureTimeframe"
                        startDateFilterName="departureDateStartAtFilter"
                        endDateFilterName="departureDateEndAtFilter"
                        startDate={localFilters.departureDateStartAtFilter}
                        endDate={localFilters.departureDateEndAtFilter}
                        setLocalFilters={setLocalFilters}
                    />
                </div>
            )}
        </>
    );
};

interface CompoundManagementModalFilterProps {
    modalType: CompoundManagementView;
}

export const CompoundManagementFilterModal = (props: CompoundManagementModalFilterProps) => {
    const dispatch = useAppDispatch();
    const modalFilter = useAppSelector(getCompoundManagementModalFilter);
    const [showModal, setShowModal] = useState(false);
    const [localFilters, setLocalFilters] = useState<LocalCompoundVehicleModalFilter>(modalFilter);

    const isFilterActive = getIsFilterActive(props.modalType, localFilters);

    const handleOnClickApply = () => {
        setLocalFilters({
            ...localFilters,
            locationFilter: getValidText(localFilters.locationFilter),
            manufacturerFilter: getValidText(localFilters.manufacturerFilter),
        });
        dispatch(
            compoundManagementSlice.actions.setCompoundManagementModalFilter({
                ...localFilters,
                arrivalDateStartAtFilter: getValidDate(localFilters.arrivalDateStartAtFilter),
                arrivalDateEndAtFilter: getValidDate(localFilters.arrivalDateEndAtFilter),
                departureDateStartAtFilter: getValidDate(localFilters.departureDateStartAtFilter),
                departureDateEndAtFilter: getValidDate(localFilters.departureDateEndAtFilter),
                locationFilter: getValidText(localFilters.locationFilter),
                manufacturerFilter: getValidText(localFilters.manufacturerFilter),
                excludeCanceledFilter: localFilters.excludeCanceledFilter,
            }),
        );
        setShowModal(false);
    };

    const handleOnClickCancel = () => {
        setLocalFilters(modalFilter);
        setShowModal(false);
    };

    const resetFilters = () => {
        setLocalFilters(UNSET_COMPOUND_VEHICLE_MODAL_FILTER);
    };

    return (
        <>
            <FilterModalButton isFilterActive={isFilterActive} setShowFilterModal={setShowModal} />
            <Dialog
                show={showModal}
                bsSize={Dialog.SIZE_SM}
                title={<FormattedMessage id="outboundOrderBook.common.table.toolbar.filter.title" />}
                body={
                    <CompoundManagementFilterModalContent
                        localFilters={localFilters}
                        setLocalFilters={setLocalFilters}
                        modalType={props.modalType}
                    />
                }
                footer={
                    <FilterModalFooter
                        handleOnClickApply={handleOnClickApply}
                        handleOnClickCancel={handleOnClickCancel}
                        resetFilters={resetFilters}
                        isApplyButtonDisabled={isApplyButtonDisabled(localFilters)}
                    />
                }
                onClose={handleOnClickCancel}
            />
        </>
    );
};

const isApplyButtonDisabled = (localFilters: LocalCompoundVehicleModalFilter): boolean => {
    const arrivalTimeframe = validateDateTimeFields(
        localFilters.arrivalDateStartAtFilter,
        localFilters.arrivalDateEndAtFilter,
    );

    const departureTimeframe = validateDateTimeFields(
        localFilters.departureDateStartAtFilter,
        localFilters.departureDateEndAtFilter,
    );

    return (
        arrivalTimeframe.hasStartDateTimeError ||
        arrivalTimeframe.hasEndDateTimeError ||
        arrivalTimeframe.hasTimeRangeOrderError ||
        departureTimeframe.hasStartDateTimeError ||
        departureTimeframe.hasEndDateTimeError ||
        departureTimeframe.hasTimeRangeOrderError
    );
};
